import Alpine from 'alpinejs'
import { formatPrice } from '@/utilities/number'
import { variantIsSubscription } from '@/utilities/commerce'
import { SUB_SKU_SUFFIX } from '@/constants/commerce'
import {
  UPDATE_ACTIVE_VARIANT,
  SET_ACTIVE_BUYING_OPTIONS,
  RESET_ACTIVE_VARIANT,
} from '@/constants/buying-panel-events'
import type { CMSProductVariant } from '@/types/sanity'

document.addEventListener('alpine:init', () => {
  const isMobileDevice = window.innerWidth < 480

  Alpine.data('variantOptions', () => ({
    availableVariants: [],
    allVariants: [],
    activeSku: '',
    showSubPricing: false,

    init() {
      this.availableVariants =
        typeof this.$el.dataset.availableVariants === 'string' &&
        JSON.parse(this.$el.dataset.availableVariants)
      this.allVariants =
        typeof this.$el.dataset.allVariants === 'string' &&
        JSON.parse(this.$el.dataset.allVariants)
    },

    eventHandlers: {
      ['@popstate.window']() {
        const urlParams = new URLSearchParams(window.location.search)
        const activeVariantId = urlParams.get('variant')

        if (activeVariantId) {
          const sku = this.getVariantSkuFromId(activeVariantId)

          if (sku) {
            const isSubscriptionSKU = sku.endsWith(SUB_SKU_SUFFIX)
            const variant = this.getVariantFromSku(sku)

            if (isSubscriptionSKU) {
              this.showSubPricing = true
            } else {
              this.showSubPricing = false
            }

            this.activeSku = sku.replace(SUB_SKU_SUFFIX, '')
            variant && this.$dispatch(RESET_ACTIVE_VARIANT, variant)
          }
        }
      },
      [`@${UPDATE_ACTIVE_VARIANT}.window`]() {
        const activeVariant = this.$event.detail

        if (!activeVariant) {
          if (!this.activeVariant) {
            this.setActiveOption(this.availableVariants[0].sku)
          }

          return
        }

        this.showSubPricing = activeVariant.sku.endsWith(SUB_SKU_SUFFIX)

        if (!this.activeSku) {
          this.setActiveOption(activeVariant.sku)
        }
      },
    },

    getRelatedSubVariant(sku: string) {
      const isSubVariant = variantIsSubscription(sku)
      return isSubVariant
        ? this.allVariants.find(
            (variant: CMSProductVariant) => variant.sku === sku,
          )
        : this.allVariants.find(
            (variant: CMSProductVariant) =>
              variant.sku === `${sku}${SUB_SKU_SUFFIX}`,
          )
    },

    getVariantFromSku(sku: string) {
      return this.allVariants.find(
        (variant: CMSProductVariant) => variant.sku === sku,
      )
    },

    getVariantSkuFromId(id: string) {
      return this.allVariants.find(
        (variant: CMSProductVariant) => variant.id == id,
      )?.sku
    },

    getRelatedPriceText(optionSku: string) {
      const subVariant = this.getRelatedSubVariant(optionSku)

      if (!subVariant || !this.showSubPricing) {
        const variant = this.getVariantFromSku(optionSku)

        return variant?.price ? formatPrice(variant.price) : ''
      }

      return `${formatPrice(subVariant.price)}/${
        isMobileDevice ? 'mo' : 'month'
      }`
    },

    setActiveOption(sku: string) {
      if (!sku) return

      this.reset()

      const isSubscriptionSKU = sku.endsWith(SUB_SKU_SUFFIX)
      const isSubcriptionVariantOnly = this.availableVariants.some(
        (variant: CMSProductVariant) => variant.sku == sku,
      )

      if (isSubscriptionSKU) this.showSubPricing = true
      if (isSubscriptionSKU && isSubcriptionVariantOnly) {
        this.activeSku = sku

        this.$dispatch(SET_ACTIVE_BUYING_OPTIONS, {
          sub: this.getRelatedSubVariant(sku),
          otp: null,
        })
        return
      }

      let activeSku = sku
      if (isSubscriptionSKU) activeSku = sku.replace(SUB_SKU_SUFFIX, '')

      const activeOption = this.availableVariants.find(
        (variant: CMSProductVariant) => variant.sku == activeSku,
      )

      this.activeSku = activeSku

      this.$dispatch(SET_ACTIVE_BUYING_OPTIONS, {
        sub: this.getRelatedSubVariant(sku),
        otp: activeOption || null,
      })
    },

    reset() {
      this.activeSku = ''
      this.showSubPricing = false
    },
  }))
})
